import { useState, useEffect } from "react";
import { Table, ConfigProvider, Button, Input, Select, Card, Modal, Pagination, Spin } from "antd";
import { FileWordOutlined, FolderViewOutlined, SearchOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import LetterForm from "./Form/LetterForm";
import { useLocation } from "react-router-dom";
import { useOfferLetter } from "../../app/hooks/useOfferLetter";
import { PaginatedResult } from "../../app/utils/pagination";
import { useStore } from "../../app/stores/store";
import Debounce from "../../app/utils/debounce";
import dayjs from "dayjs";

const debouncer = new Debounce();
const { Option } = Select;

const OfferLetterApplicantList = () => {
  const { notificationStore } = useStore();
  const { showNotification } = notificationStore;
  const [paginatedResult, setPaginatedResult] = useState(new PaginatedResult());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const location = useLocation();
  const status = location.state?.status || "Declined";
  const pageSize = 10;

  const positionOptions = Array.from(new Set((paginatedResult.data || []).map((job) => job.position).filter(Boolean)));

  const handleView = (applicant) => {
    setSelectedApplicant(applicant);
    setIsModalVisible(true);
  };

  const { getApplicants } = useOfferLetter({
    status: status,
    pageSize: pageSize,
  });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    debouncer.debounce({
      debounce: () => {
        setCurrentPage(1);
        setDebouncedSearch(value);
      },
      debounceTimeout: 800,
    });
  };

  const columns = [
    { title: "NO.", dataIndex: "no", key: "no", align: "center", render: (_, __, index) => <span>{(currentPage - 1) * pageSize + index + 1}</span> },
    { title: "DATE", dataIndex: "createdAt", key: "createdAt", align: "center", render: (date) => <p>{dayjs(date).format("MMM DD, YYYY")}</p> },
    { title: "NAME", dataIndex: "fullName", key: "fullName", align: "center" },
    { title: "POSITION", dataIndex: "position", key: "position", align: "center" },
    {
      title: "",
      key: "action",
      align: "center",
      render: (_, record) => (
        <div className="flex">
          {status === "Offered" && (
            <Button
              type="primary"
              className="mx-auto flex justify-center items-center hover:opacity-80"
              onClick={() => handleView(record)}
              style={{ backgroundColor: "#F97316", borderColor: "#F97316", fontWeight: "600", borderRadius: "9999px" }}
            >
              <FolderViewOutlined />
              View Offer
            </Button>
          )}
          {status !== "Offered" && status !== "Declined" && (
            <Button
              type="primary"
              className="mx-auto flex justify-center items-center hover:opacity-80"
              onClick={() => handleView(record)}
              style={{ backgroundColor: "#F97316", borderColor: "#F97316", fontWeight: "600", borderRadius: "9999px" }}
            >
              <FileWordOutlined /> Create Offer
            </Button>
          )}
        </div>
      ),
    },
  ];
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getApplicants(status, currentPage, pageSize, debouncedSearch || selectedPosition);
      setPaginatedResult(result);
    } catch (error) {
      showNotification(error, "error", {
        title: "Error loading applicants",
        duration: 5000,
      });
      setPaginatedResult(new PaginatedResult());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, debouncedSearch, selectedPosition]);

  return (
    <>
      <Card className="rounded-2xl px-4 md:px-16 py-8 shadow-2xl w-full overflow-hidden">
        <div className="flex justify-between flex-col md:flex-row md:gap-8 items-center mb-6">
          <div className="flex flex-col md:flex-row gap-8">
            <Input
              size="large"
              placeholder="Search here"
              value={searchText}
              onChange={handleSearchChange}
              prefix={<SearchOutlined style={{ fontSize: "20px", color: "#666" }} />}
            />
            <Select
              size="large"
              placeholder="Search position"
              className="w-full md:w-72"
              value={selectedPosition}
              allowClear
              onChange={(value) => {
                setSelectedPosition(value);
                setCurrentPage(1);
              }}
            >
              {positionOptions.map((position) => (
                <Option key={position} value={position}>
                  {position}
                </Option>
              ))}
            </Select>
          </div>
          <h2 className="text-2xl font-medium m-0">
            {paginatedResult.data.length} {status === "Declined" ? "Rejected" : status}
          </h2>
        </div>

        <ConfigProvider
          theme={{
            components: {
              Table: { headerBg: "#364042", headerColor: "white" },
            },
          }}
        >
          {isLoading ? (
            <div className="col-span-3 flex justify-center py-20">
              <Spin tip="Fetching Job Openings..." />
            </div>
          ) : (
            <Table rowKey="id" columns={columns} dataSource={paginatedResult.data} pagination={false} />
          )}
          <div className="flex justify-center mt-8">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              className="job-search-pagination"
              total={paginatedResult.totalItems}
              onChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              showSizeChanger={false}
            />
          </div>
        </ConfigProvider>
      </Card>

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        centered
        destroyOnClose
        className="offer-letter-modal"
        styles={{ body: { padding: 0 } }}
      >
        <LetterForm applicant={selectedApplicant} onClose={() => setIsModalVisible(false)} onSuccess={fetchData} status={status} />
      </Modal>
    </>
  );
};

export default observer(OfferLetterApplicantList);
